import {Component, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {Observable} from 'rxjs';
import {BsDropdownDirective} from 'ngx-bootstrap/dropdown';
import {FormControl} from '@angular/forms';
import {NavigationEnd} from "@angular/router";
import {HeaderComponent} from "../../header/header.component";

@Component({
    selector: 'core-top-navigation',
    templateUrl: './top-nav.component.html',
    standalone: false
})

export class TopNavComponent extends HeaderComponent implements OnInit {
    @ViewChildren(BsDropdownDirective) dropdowns?: QueryList<BsDropdownDirective>;
    @ViewChild('navbarDropdown') navbarDropdown?: ElementRef;
    @ViewChild('fixedMobileNavFooter') fixedMobileNavFooter?: ElementRef;

    pillars$!: Observable<any>;

    delayedLoaded: boolean = false;
    activeMenuTab = new FormControl(0);
    fragment?: string;
    resizeTimeout: any;
    clickIndexNo?: void | number;

    // Menu variables
    content$?: Observable<any>;
    public openDropdown: any;

    @HostListener('window:resize', ['$event']) onResize(event: Event) {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = setTimeout((() => {
            this.isDesktopCheck(event.target);
        }), 200);
    }

    // Close dropdown menu on escape click
    @HostListener('keydown.esc')
    onEsc() {
        if (this.dropdownIsOpen && this.dropdowns) {
            this.dropdowns.forEach((dropdown: BsDropdownDirective) => {
                // dropdown from BsDropdownDirective is private so assigning it to public var to allow us to use _elementRef below
                this.openDropdown = dropdown;
                // Check if current iteration is our opened one
                if (this.openDropdown._elementRef.nativeElement.classList.contains('show')) {
                    // Add focus manually back on navbar link
                    this.openDropdown._elementRef.nativeElement.firstChild.focus();
                }
                this.openDropdown.hide()
            });
        }
    }

    selectedMobNavLinkIndex: number | undefined;
    isDesktop: boolean = true;
    isCategoryNav?: boolean;
    resetMobileScroll?: boolean;
    isMobileNavFooter: boolean = true; // Boolean to use a fixed footer element for cta at bottom of mobile nav

    contactEnabled!: boolean;
    dropdownIsOpen!: boolean;

    override ngOnInit(): void {
        super.ngOnInit();
        this.content$ = this.client.getPageCategories();

        this.api.zoneChangeEmit.subscribe(() => {
            this.pillars$ = this.client.getPillars();
        });

        this.subscriptions.push(this.settings.showBenefitCategories().subscribe((state: any) => {
            this.isCategoryNav = state;
        }));

        this.subscriptions.push(this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.updateLogoUrl();
            }
        }));

        this.isDesktopCheck(window);

        this.subscriptions.push(this.settings.isContactEnabled().subscribe((state: any) => {
            this.contactEnabled = state;
        }));
    }

    onOpenChange(isOpen: boolean, index: number): void {
        const overlayElem = document.querySelector('.page-overlay');

        if (isOpen && overlayElem) {
            this.renderer.addClass(overlayElem, 'page-overlay--is-active');
            this.clickIndexNo = index;
        } else if (this.clickIndexNo === index) { // only close page overlay when the same link is clicked twice
            this.renderer.removeClass(overlayElem, 'page-overlay--is-active');
        }
    }

    onDropdownOpen() {
        return this.dropdownIsOpen = true;
    }

    menuStateCheck(menuState: boolean, event: Event) {
        this.closeChildNav(event);
        if (this.dropdowns) {
            this.hideDropdowns();
        }
        let tmpOverflow = menuState ? 'revert' : 'hidden';
        document.body.style.setProperty('overflow-y', tmpOverflow, 'important');
    }

    toggleMobileMenu(evt: Event) {
        this.isCollapsed = !this.isCollapsed;
        this.menuStateCheck(this.isCollapsed, evt);

        // Ensures the logo is correct colour when mobile nav open/closed for transparent nav on
        if (this.isTransparentNav) {
            if (!this.darkMode) {
                this.logoUrl = !this.isCollapsed ? this.logoInvertedUrl : this.logoTransparentUrl;
            } else {
                // only need to show white logo in dark mode when transparent nav on
                this.logoUrl = this.logoTransparentUrl;
            }
        }
    }

    closeChildNav(event?: Event) {
        // Setting value to number that isn't the index to remove is-active class on mobile benefits submenu
        this.selectedMobNavLinkIndex = 99;
        // Preventing click propagation. This keeps the mobile Benefits accordion open when a submenu is closed
        if (event) {
            event.stopPropagation();
        }
        // Add main scrollbar back if required
        this.resetMobileScroll = false;
    }

    setTopNavActiveLinkState(page: any) {
        let setActiveState = false;
        // If page has children and child page is current page, set active link on parent page link
        if (page.children && page.children.length > 0) {
            for (let i = 0; i < page.children.length; i++) {
                const childPageRoute = '/' + page.children[i].url;
                if (this.router.url === childPageRoute) {
                    setActiveState = true;
                }
            }
        } else {
            // No child pages
            // Home page check as this.router.url and page.url won't match
            if (this.router.url === '/' && page.url === 'default') {
                setActiveState = true
            } else {
                // Set link to active if link page and current page match for page that aren't home and don't have children
                const pageRoute = '/' + page.url;
                setActiveState = this.router.url === pageRoute;
            }
        }
        return setActiveState;
    }

    setDropdownActiveLinkState(page: any) {
        // Set dropdown (child) link to active if link page and current page match
        const pageRoute = '/' + page.url;
        return this.router.url === pageRoute;
    }

    isDesktopCheck(elem: any) {
        if (elem.innerWidth >= 992) {
            if (!this.isCollapsed) {
                this.isCollapsed = true;
            }
            this.isDesktop = true;
        } else {
            this.isDesktop = false;
        }

        this.setMobChildNavHeight();
    }

    setMobChildNavHeight() {
        // 100vh fix for iOS and Android
        // We effectively calculate the left over real-estate for the scrolling sub-menu of pages.
        // Yes I know, I know...
        // Current value is 374px
        // 91px - Header bar
        // 118px - Search section (includes margin)
        // 70px - 'Back' link
        // 94px - Country Selector

        // Changing space needed at bottom of mob nav if we need it for a cta button
        let offset = this.isMobileNavFooter ? 374 : 320;
        let vh = window.innerHeight - offset;
        document.documentElement.style.setProperty('--vhcustom-child-menu', `${vh}px`);

        // LifeMoment height fix for smaller screens
        // See above
        // 90px Header Bar
        // 67px Navigation Bar
        // 60px Padding (30px top/bottom)
        // let vhlm = window.innerHeight - 90 - 67 - 60
        // document.documentElement.style.setProperty('--vhcustom-lifemoments', `${vhlm}px`);
    }

    // This handles changing pages. It's shared across all screen sizes.
    // 1: It will automatically close the dropdown menu on Desktop
    // 2: It will also remove the mobile styling if user navigates page on mobile
    // 3: If the menu is open on mobile it will close it
    // 6: Will navigate to correct page
    navigateToPage(page: string) {
        if (this.dropdowns) {
            this.hideDropdowns();
            this.onOpenChange(false, -1);
        }
        document.body.style.overflowY = 'revert';
        if (!this.isCollapsed) {
            this.isCollapsed = true;
        }

        if (page && page.indexOf('#') > -1) {
            // Contains HASH!
            const urlArray = page.split('#');
            this.router.navigate([urlArray[0]], {fragment: urlArray[1]});
        } else {
            // No Hash, proceed as usual
            if (page === 'default') {
                page = '';
            }
            this.router.navigate(['/' + page]);
        }
    }

    closeChildDropdown() {
        if (this.dropdowns) {
            this.hideDropdowns();
            this.onOpenChange(false, -1);
        }
    }

    override updateLogoUrl() {
        // Home page only
        if (this.router.url === '/' || this.router.url === '/default') {
            if (this.showTransparentLogo() ||
                (this.showTransparentLogoForDarkModeViaSystemPref() &&
                    !this.showDarkLogoForLightModeViaSystemPref())) {
                this.logoUrl = this.logoTransparentUrl;
            } else {
                this.logoUrl = this.logoInvertedUrl;
            }
        } else {
            // All other pages
            // Only checking if hero images are enabled when not on the home page
            if (this.showTransparentLogo() &&
                this.heroImagesEnabled ||
                this.darkMode && !this.heroImagesEnabled ||
                this.showTransparentLogoForDarkModeViaSystemPref() &&
                !this.showDarkLogoForLightModeViaSystemPref()
            ) {
                if ((this.page && this.page.meta && !this.page.meta.icon)
                    || (this.post && this.post.data && !this.post.data.image)
                ) {
                    this.logoUrl = this.logoInvertedUrl;
                } else {
                    this.logoUrl = this.logoTransparentUrl;
                }
            } else {
                this.logoUrl = this.logoInvertedUrl;
            }
        }
    }

    showTransparentLogo() {
        return (this.isTransparentNav &&
                this.isCollapsed &&
                !this.isTopNavTransparentSticky) ||
            this.darkMode;
    }

    showTransparentLogoForDarkModeViaSystemPref() {
        return this.darkMode &&
            this.isTopNavTransparentSticky ||
            this.settings.isSystemPreferenceDarkModeOn() ||
            window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    showDarkLogoForLightModeViaSystemPref() {
        return !this.darkMode &&
            this.isTopNavTransparentSticky ||
            this.settings.isSystemPreferenceLightModeOn() ||
            window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ||
            !this.isTransparentNav &&
            !this.darkMode;
    }

    stopPropagation($event: MouseEvent) {
        return $event.stopPropagation();
    }

    parsePillarClass(pillar: string) {
        return pillar.replace(new RegExp(' ', 'g'), '-').replace(new RegExp('/', 'g'), '-').toLowerCase();
    }

    hideDropdowns() {
        this.dropdowns?.forEach((dropdown: BsDropdownDirective) => {
            dropdown.hide()
        });
    }
}
